/* eslint-disable react-hooks/immutability */
import { faTrashXmark } from "@fortawesome/pro-regular-svg-icons";
import { faClose, faSave, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useForm } from "../../../components/FormProvider";
import { Family } from "../../../models";

const PAYMENT_FREQUENCY_WEEKLY = "Weekly";
const PAYMENT_FREQUENCY_BIWEEKLY = "BiWeekly";
const PAYMENT_FREQUENCY_MONTHLY = "Monthly";

export function EditFamilyForm(props: {
  family?: Family;
  resetFormRef: React.RefObject<(() => void) | null>;
  dataGetterRef: React.RefObject<(() => Family | null) | null>;
}) {
  const { family } = props;
  const form = useForm();

  const [paymentFrequency, setPaymentFrequency] = useState<string>(family?.paymentFrequency ?? PAYMENT_FREQUENCY_WEEKLY);

  // use refs for the text fields to improve performance. otherwise every character entered/deleted causes a re-render, making it feel very slow
  // it's not as noticable with the other types of inputs, so we don't need to make those ref based.
  const nameField = useRef<HTMLInputElement | null>(null);
  const payerNameField = useRef<HTMLInputElement | null>(null);
  const addressField = useRef<HTMLInputElement | null>(null);
  const cityField = useRef<HTMLInputElement | null>(null);
  const stateField = useRef<HTMLInputElement | null>(null);
  const zipCodeField = useRef<HTMLInputElement | null>(null);
  const phoneField = useRef<HTMLInputElement | null>(null);

  const resetForm = (): void => {
    setPaymentFrequency(PAYMENT_FREQUENCY_WEEKLY);
    if (nameField.current) {
      nameField.current.value = family?.name ?? "";
    }
    if (payerNameField.current) {
      payerNameField.current.value = family?.payerName ?? "";
    }
    if (addressField.current) {
      addressField.current.value = family?.address ?? "";
    }
    if (cityField.current) {
      cityField.current.value = family?.city ?? "";
    }
    if (stateField.current) {
      stateField.current.value = family?.state ?? "";
    }
    if (zipCodeField.current) {
      zipCodeField.current.value = family?.zipCode ?? "";
    }
    if (phoneField.current) {
      phoneField.current.value = family?.phone ?? "";
    }
  };

  const getUpdatedRecord = (): Family => {
    return {
      id: family?.id ?? 0,
      name: nameField?.current?.value ?? "",
      paymentFrequency,
      payerName: payerNameField?.current?.value ?? "",
      address: addressField?.current?.value ?? "",
      city: cityField?.current?.value ?? "",
      state: (stateField?.current?.value ?? "").toUpperCase(),
      zipCode: zipCodeField?.current?.value ?? "",
      phone: phoneField?.current?.value ?? "",
      deleted: false,
      children: [],
      communicationRecipients: [],
      hasActiveChildren: false,
      mobilePin: "",
      createdCentralDateTime: "2025-01-01", // doesn't matter what this is because the api will override it
      modifiedCentralDateTime: "2025-01-01", // doesn't matter what this is because the api will override it
    };
  };

  useEffect(() => {
    if (props.dataGetterRef) {
      props.dataGetterRef.current = getUpdatedRecord;
    }
    if (props.resetFormRef) {
      props.resetFormRef.current = resetForm;
    }
  });

  useEffect(() => {
    setPaymentFrequency(family?.paymentFrequency ?? PAYMENT_FREQUENCY_BIWEEKLY);
  }, [family]);

  const handlePaymentFrequencyChange = (event: SelectChangeEvent) => {
    setPaymentFrequency(event.target.value as string);
  };

  return (
    <Stack gap={2} flexGrow={1}>
      <Stack direction="row" alignItems="center">
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {family ? "Edit Family" : "Add Family"}
        </Typography>
        <IconButton sx={{ width: 44, height: 44 }} onClick={form.cancel}>
          <FontAwesomeIcon icon={faClose} />
        </IconButton>
      </Stack>

      <Stack sx={{ alignItems: { xs: "center", md: "flex-start" } }} flexGrow={1}>
        <Stack gap={1}>
          <Stack direction="row" gap={1}>
            <TextField variant="filled" label="Name" defaultValue={family?.name ?? ""} inputRef={nameField} disabled={form.isDisabled} />
          </Stack>
          <FormControl variant="filled" sx={{ width: 180 }} disabled={form.isDisabled}>
            <InputLabel id="payment-frequency-label">Payment Frequency</InputLabel>
            <Select
              labelId="payment-frequency-label"
              id="payment-frequency"
              value={paymentFrequency}
              onChange={handlePaymentFrequencyChange}
              MenuProps={{
                slotProps: {
                  root: { sx: { zIndex: 1600 } },
                  paper: { sx: { zIndex: 1600 } },
                },
              }}
            >
              <MenuItem value={PAYMENT_FREQUENCY_WEEKLY}>Weekly</MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY_BIWEEKLY}>Bi-weekly</MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY_MONTHLY}>Monthly</MenuItem>
            </Select>
          </FormControl>
          <Stack direction="row" gap={1}>
            <TextField
              variant="filled"
              label="Payer"
              sx={{ width: 300 }}
              defaultValue={family?.payerName ?? ""}
              inputRef={payerNameField}
              disabled={form.isDisabled}
            />
          </Stack>
          <Stack direction="row" gap={1}>
            <TextField
              variant="filled"
              label="Address"
              sx={{ width: 300 }}
              defaultValue={family?.address ?? ""}
              inputRef={addressField}
              disabled={form.isDisabled}
            />
          </Stack>
          <Stack direction="row" gap={1}>
            <TextField variant="filled" label="City" sx={{ width: 150 }} defaultValue={family?.city ?? ""} inputRef={cityField} disabled={form.isDisabled} />
            <TextField variant="filled" label="State" sx={{ width: 70 }} defaultValue={family?.state ?? ""} inputRef={stateField} disabled={form.isDisabled} />
            <TextField
              variant="filled"
              label="Zip Code"
              sx={{ width: 100 }}
              defaultValue={family?.zipCode ?? ""}
              inputRef={zipCodeField}
              disabled={form.isDisabled}
            />
          </Stack>
          <Stack direction="row" gap={1}>
            <TextField variant="filled" label="Phone" defaultValue={family?.phone ?? ""} inputRef={phoneField} disabled={form.isDisabled} />
          </Stack>
        </Stack>
      </Stack>

      <Button variant="contained" onClick={form.save} sx={{ height: 50, width: { xs: "100%", md: "inherit" } }} startIcon={<FontAwesomeIcon icon={faSave} />}>
        Save Changes
      </Button>

      {!family && (
        <Button
          variant="outlined"
          color="error"
          onClick={form.cancel}
          sx={{ height: 50, width: { xs: "100%", md: "inherit" } }}
          startIcon={<FontAwesomeIcon icon={faTimes} />}
        >
          Cancel
        </Button>
      )}

      {family && (
        <Button
          variant="outlined"
          color="error"
          onClick={form.delete}
          sx={{ height: 50, width: { xs: "100%", md: "inherit" } }}
          startIcon={<FontAwesomeIcon icon={faTrashXmark} />}
        >
          Delete
        </Button>
      )}
    </Stack>
  );
}
